/* eslint-disable jsx-a11y/interactive-supports-focus */
import { Alert, Button } from 'grab-ui';
import translateDateTime from 'helper/date-helpers';
import I18n from 'i18n-js';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { toggleMaintenanceBannerAction } from '../../../../actions/app-actions';
import styles from '../styles.less';

const MaintenanceBanner = ({ notification, maintenanceBannerDismissed }) => {
  const dispatch = useDispatch();
  const dismissNoticeBannerForSessionKey = 'dismissNoticeBannerForSession';
  const startTimeStamp = notification.startUTCDateTime * 1000;
  const endTimeStamp = notification.endUTCDateTime * 1000;

  const hasUserDismissedBanner = useMemo(
    () => sessionStorage.getItem(dismissNoticeBannerForSessionKey),
    [dismissNoticeBannerForSessionKey],
  );

  const hasNotificationExpired = useMemo(() => endTimeStamp < Date.now(), [
    endTimeStamp,
  ]);
  const isMaintenanceOngoing = useMemo(() => startTimeStamp < Date.now(), [
    startTimeStamp,
  ]);

  // Used to toggle the maintenance banner visibility, controls Homepage Background CSS
  // Pass true to dismiss the banner, false to show it
  useEffect(
    () => {
      dispatch(
        toggleMaintenanceBannerAction(
          hasUserDismissedBanner || hasNotificationExpired,
        ),
      );
    },
    [
      toggleMaintenanceBannerAction,
      hasUserDismissedBanner,
      hasNotificationExpired,
    ],
  );

  const getFormattedMessageCopy = useMemo(
    () => {
      const wantDateTimeFormat = 'dddd, Do MMM YYYY HH:mm a';
      const messageType = isMaintenanceOngoing ? 'ongoing' : 'upcoming';
      return I18n.t(`appBar.noticeBanner.message.${messageType}`, {
        startDateTime: translateDateTime(startTimeStamp, wantDateTimeFormat),
        endDateTime: translateDateTime(endTimeStamp, wantDateTimeFormat),
      });
    },
    [startTimeStamp, endTimeStamp, isMaintenanceOngoing],
  );

  const isInvalidTimeStamp = useCallback(
    (startTimeStamp, endTimeStamp) =>
      startTimeStamp === 0 ||
      endTimeStamp === 0 ||
      startTimeStamp === endTimeStamp ||
      startTimeStamp > endTimeStamp,
    [],
  );

  if (
    maintenanceBannerDismissed ||
    isInvalidTimeStamp(startTimeStamp, endTimeStamp)
  ) {
    return null;
  }

  const formattedMessage = (
    <div className={styles.message}>
      {getFormattedMessageCopy}
      {notification.dismissible &&
        !isMaintenanceOngoing && (
          <Button
            type="ghost"
            size="small"
            ghost
            onClick={() => {
              sessionStorage.setItem(dismissNoticeBannerForSessionKey, true);
              dispatch(toggleMaintenanceBannerAction(true));
            }}
            className={styles.dismissButton}
          >
            {I18n.t('appBar.noticeBanner.dontShowMeAgain')}
          </Button>
        )}
    </div>
  );

  return (
    <div className={styles.container}>
      <Alert
        type={notification.type}
        message={formattedMessage}
        onClose={() => dispatch(toggleMaintenanceBannerAction(true))}
        showIcon
        closable={!isMaintenanceOngoing}
      />
    </div>
  );
};

MaintenanceBanner.defaultProps = {
  notification: {
    dismissible: false,
    type: 'info',
    startUTCDateTime: 0,
    endUTCDateTime: 0,
    message: '',
    locale: '',
  },
  maintenanceBannerDismissed: false,
};

MaintenanceBanner.propTypes = {
  notification: PropTypes.shape({
    dismissible: PropTypes.bool.isRequired,
    type: PropTypes.string.isRequired,
    startUTCDateTime: PropTypes.number.isRequired,
    endUTCDateTime: PropTypes.number.isRequired,
  }).isRequired,
  maintenanceBannerDismissed: PropTypes.bool.isRequired,
};

export default withRouter(MaintenanceBanner);
